import { LineChart } from '@/components/dashboard/Charts';
import {
  EmptyChartState,
  SectionHeader,
  Skeleton,
} from '@/components/dashboard/DashboardCards';
import { ErrorState } from '@/components/ui/States';
import { formatMoney } from '@/components/dashboard/commandCenterConfig';
import type { ReactNode } from 'react';

export function RevenuePerformance({
  grossSale,
  series,
  loading,
  error,
  onRetry,
  aside,
}: {
  grossSale: number | null;
  series: Array<{ label: string; value: number }>;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  aside?: ReactNode;
}) {
  const hasData = series.length > 0 && series.some((p) => (p.value || 0) > 0);

  return (
    <section className="cmd-section revenue-panel card" aria-label="Revenue and performance">
      <SectionHeader
        title="Revenue & Performance"
        aside={aside}
      />

      <div className="revenue-summary">
        <div>
          <p className="revenue-label">Gross Sales</p>
          <p className="revenue-value">
            {loading && grossSale == null ? (
              <span className="skeleton" style={{ height: 32, width: 140, display: 'inline-block' }} />
            ) : (
              formatMoney(grossSale)
            )}
          </p>
        </div>
        <div className="revenue-legend" aria-hidden>
          <span className="legend-dot" />
          <span className="muted">Gross sales over time</span>
        </div>
      </div>

      {error ? (
        <ErrorState description="Unable to load sales data" onRetry={onRetry} />
      ) : loading && series.length === 0 ? (
        <Skeleton height={220} />
      ) : !hasData ? (
        <EmptyChartState
          title="No sales data yet"
          description="Sales information will appear when orders are completed."
        />
      ) : (
        <LineChart
          bare
          title="Gross sales"
          points={series.map((s) => ({ label: s.label, value: s.value }))}
          color="#0d9488"
          format={(v) => formatMoney(v)}
        />
      )}
      <p className="sr-only">
        Gross sales chart. Current value {grossSale ?? 0}. Points:{' '}
        {series.map((s) => `${s.label} ${s.value}`).join(', ') || 'none'}.
      </p>
    </section>
  );
}
